import { comparer, makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { isPermissionError } from "@/lib/is-permission-error";
import type { RootStore } from "./root.store";
import type {
  TCreateProjectViewRequest,
  TCreateWorkspaceViewRequest,
  TProjectView,
  TProjectViewListFilters,
  TUpdateProjectViewRequest,
  TUpdateWorkspaceViewRequest,
  TWorkspaceView,
} from "@/types/view";

/** `forbidden` is a list fetch the API refused with a 403 (NG-2153): the role
 *  lacks `saved_view.read`, so asking again cannot succeed. The Views list pages
 *  read it to explain the refusal instead of offering Retry. */
type TAsyncState = "idle" | "loading" | "success" | "error" | "forbidden";

const defaultProjectViewListFilters = (): TProjectViewListFilters => ({
  search: "",
  sortBy: "default",
  favoritesOnly: false,
});

/**
 * Normalize a workspace view as it enters the store.
 *
 * An absent `kind` means `work_item`, matching the backend default. The field can
 * legitimately be missing — a payload cached before NG-528, or the deploy gap where
 * a new frontend meets the old backend — and every reader downstream (the kind
 * badge, the hub's kind filter, the detail page's per-kind dispatch) would
 * otherwise need its own answer for that case. One answer, at the boundary.
 */
function normalizeWorkspaceView(view: TWorkspaceView): TWorkspaceView {
  return view.kind ? view : { ...view, kind: "work_item" };
}

/**
 * The keys of an update whose value differs from the stored view:
 * `view_updated`'s `fields`. The edit dialogs send name and description together,
 * so the body's keys alone would name both on every save. A view that is not in
 * the map yet counts every key it sends.
 */
function changedViewFields(
  body: TUpdateProjectViewRequest | TUpdateWorkspaceViewRequest,
  before: TProjectView | TWorkspaceView | undefined,
): string[] {
  return Object.keys(body).filter((key) => {
    if (!before) return true;
    return !comparer.structural(
      (body as Record<string, unknown>)[key],
      (before as Record<string, unknown>)[key],
    );
  });
}

export class ViewStore {
  projectViewMap = observable.map<string, TProjectView>();
  workspaceViewMap = observable.map<string, TWorkspaceView>();
  projectViewIdsByProject = observable.map<string, string[]>();
  workspaceViewIdsByWorkspace = observable.map<string, string[]>();
  listFiltersByProject = observable.map<string, TProjectViewListFilters>();
  fetchStateByKey = observable.map<string, TAsyncState>();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  getProjectViews(projectId: string): TProjectView[] {
    return (this.projectViewIdsByProject.get(projectId) ?? [])
      .map((viewId) => this.projectViewMap.get(viewId))
      .filter((v): v is TProjectView => Boolean(v));
  }

  getFilteredProjectViews(projectId: string): TProjectView[] {
    const filters = this.getProjectListFilters(projectId);
    let views = [...this.getProjectViews(projectId)];

    if (filters.search.trim()) {
      const query = filters.search.trim().toLowerCase();
      views = views.filter(
        (view) =>
          view.name.toLowerCase().includes(query) || view.description.toLowerCase().includes(query),
      );
    }

    if (filters.favoritesOnly) {
      views = views.filter((view) => view.is_favorite);
    }

    views.sort((left, right) => {
      if (filters.sortBy === "name") {
        return left.name.localeCompare(right.name);
      }

      if (filters.sortBy === "created_at") {
        return right.created_at.localeCompare(left.created_at);
      }

      if (filters.sortBy === "updated_at") {
        return right.updated_at.localeCompare(left.updated_at);
      }

      if (left.is_favorite !== right.is_favorite) {
        return left.is_favorite ? -1 : 1;
      }

      return left.name.localeCompare(right.name);
    });

    return views;
  }

  getWorkspaceViews(workspaceSlug: string): TWorkspaceView[] {
    return (this.workspaceViewIdsByWorkspace.get(workspaceSlug) ?? [])
      .map((viewId) => this.workspaceViewMap.get(viewId))
      .filter((v): v is TWorkspaceView => Boolean(v));
  }

  getProjectView(viewId: string): TProjectView | undefined {
    return this.projectViewMap.get(viewId);
  }

  getWorkspaceView(viewId: string): TWorkspaceView | undefined {
    return this.workspaceViewMap.get(viewId);
  }

  getProjectListFilters(projectId: string): TProjectViewListFilters {
    return this.listFiltersByProject.get(projectId) ?? defaultProjectViewListFilters();
  }

  getFetchState(key: string): TAsyncState | undefined {
    return this.fetchStateByKey.get(key);
  }

  setProjectListFilters(projectId: string, value: Partial<TProjectViewListFilters>): void {
    this.listFiltersByProject.set(projectId, {
      ...this.getProjectListFilters(projectId),
      ...value,
    });
  }

  async fetchProjectViews(workspaceSlug: string, projectId: string): Promise<void> {
    const key = `project-list:${projectId}`;
    this.fetchStateByKey.set(key, "loading");
    try {
      const views = await this.rootStore.viewService.listProjectViews(workspaceSlug, projectId);
      runInAction(() => {
        views.forEach((view) => {
          this.projectViewMap.set(view.id, view);
        });
        this.projectViewIdsByProject.set(
          projectId,
          views.map((view) => view.id),
        );
        this.fetchStateByKey.set(key, "success");
      });
    } catch (error) {
      runInAction(() => {
        this.fetchStateByKey.set(key, isPermissionError(error) ? "forbidden" : "error");
      });
      throw error;
    }
  }

  async fetchProjectView(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
  ): Promise<TProjectView> {
    const key = `project-view:${projectId}:${viewId}`;
    this.fetchStateByKey.set(key, "loading");
    try {
      const view = await this.rootStore.viewService.getProjectView(
        workspaceSlug,
        projectId,
        viewId,
      );
      runInAction(() => {
        // The map only: the list index is the list endpoint's (and create's) to
        // write, so a view opened from a shared link is never listed (NG-2263).
        this.projectViewMap.set(view.id, view);
        this.fetchStateByKey.set(key, "success");
      });
      return view;
    } catch (error) {
      runInAction(() => {
        this.fetchStateByKey.set(key, "error");
      });
      throw error;
    }
  }

  async createProjectView(
    workspaceSlug: string,
    projectId: string,
    data: TCreateProjectViewRequest,
    source: AnalyticsSource = "page",
  ): Promise<TProjectView> {
    const view = await this.rootStore.viewService.createProjectView(workspaceSlug, projectId, data);
    runInAction(() => {
      this.projectViewMap.set(view.id, view);
      const ids = new Set(this.projectViewIdsByProject.get(projectId) ?? []);
      ids.add(view.id);
      this.projectViewIdsByProject.set(projectId, Array.from(ids));
    });
    track("view_created", { view_id: view.id, scope: "project", project_id: projectId, source });
    return view;
  }

  async updateProjectView(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
    data: TUpdateProjectViewRequest,
    source: AnalyticsSource = "page",
  ): Promise<TProjectView> {
    // Read before the write replaces it: `fields` is what this save changed.
    const fields = changedViewFields(data, this.projectViewMap.get(viewId));
    const view = await this.rootStore.viewService.updateProjectView(
      workspaceSlug,
      projectId,
      viewId,
      data,
    );
    runInAction(() => {
      this.projectViewMap.set(view.id, view);
    });
    track("view_updated", {
      view_id: viewId,
      scope: "project",
      project_id: projectId,
      fields,
      source,
    });
    return view;
  }

  async deleteProjectView(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.viewService.deleteProjectView(workspaceSlug, projectId, viewId);
    runInAction(() => {
      this.projectViewMap.delete(viewId);
      this.projectViewIdsByProject.set(
        projectId,
        (this.projectViewIdsByProject.get(projectId) ?? []).filter((id) => id !== viewId),
      );
    });
    track("view_deleted", { view_id: viewId, scope: "project", project_id: projectId, source });
  }

  async favoriteProjectView(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
  ): Promise<void> {
    const current = this.projectViewMap.get(viewId);
    if (!current) return;

    runInAction(() => {
      this.projectViewMap.set(viewId, { ...current, is_favorite: true });
    });

    try {
      await this.rootStore.favorite.addFavorite(workspaceSlug, "view", viewId, projectId);
    } catch (error) {
      runInAction(() => {
        this.projectViewMap.set(viewId, current);
      });
      throw error;
    }
  }

  async unfavoriteProjectView(
    workspaceSlug: string,
    projectId: string,
    viewId: string,
  ): Promise<void> {
    const current = this.projectViewMap.get(viewId);
    if (!current) return;

    runInAction(() => {
      this.projectViewMap.set(viewId, { ...current, is_favorite: false });
    });

    try {
      await this.rootStore.favorite.removeFavoriteByEntity(workspaceSlug, viewId);
    } catch (error) {
      runInAction(() => {
        this.projectViewMap.set(viewId, current);
      });
      throw error;
    }
  }

  async fetchWorkspaceViews(workspaceSlug: string): Promise<void> {
    const key = `workspace-list:${workspaceSlug}`;
    this.fetchStateByKey.set(key, "loading");
    try {
      const views = await this.rootStore.viewService.listWorkspaceViews(workspaceSlug);
      runInAction(() => {
        views.forEach((view) => {
          this.workspaceViewMap.set(view.id, normalizeWorkspaceView(view));
        });
        this.workspaceViewIdsByWorkspace.set(
          workspaceSlug,
          views.map((view) => view.id),
        );
        this.fetchStateByKey.set(key, "success");
      });
    } catch (error) {
      runInAction(() => {
        this.fetchStateByKey.set(key, isPermissionError(error) ? "forbidden" : "error");
      });
      throw error;
    }
  }

  async fetchWorkspaceView(workspaceSlug: string, viewId: string): Promise<TWorkspaceView> {
    const key = `workspace-view:${workspaceSlug}:${viewId}`;
    this.fetchStateByKey.set(key, "loading");
    try {
      const view = await this.rootStore.viewService.getWorkspaceView(workspaceSlug, viewId);
      runInAction(() => {
        // The map only: the list index is the list endpoint's (and create's) to
        // write, so a view opened from a shared link is never listed (NG-2263).
        this.workspaceViewMap.set(view.id, normalizeWorkspaceView(view));
        this.fetchStateByKey.set(key, "success");
      });
      return view;
    } catch (error) {
      runInAction(() => {
        this.fetchStateByKey.set(key, "error");
      });
      throw error;
    }
  }

  async createWorkspaceView(
    workspaceSlug: string,
    data: TCreateWorkspaceViewRequest,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceView> {
    const view = await this.rootStore.viewService.createWorkspaceView(workspaceSlug, data);
    runInAction(() => {
      this.workspaceViewMap.set(view.id, normalizeWorkspaceView(view));
      const ids = new Set(this.workspaceViewIdsByWorkspace.get(workspaceSlug) ?? []);
      ids.add(view.id);
      this.workspaceViewIdsByWorkspace.set(workspaceSlug, Array.from(ids));
    });
    track("view_created", {
      view_id: view.id,
      scope: "workspace",
      kind: normalizeWorkspaceView(view).kind,
      source,
    });
    return view;
  }

  async updateWorkspaceView(
    workspaceSlug: string,
    viewId: string,
    data: TUpdateWorkspaceViewRequest,
    source: AnalyticsSource = "page",
  ): Promise<TWorkspaceView> {
    // Read before the write replaces it: `fields` is what this save changed.
    const fields = changedViewFields(data, this.workspaceViewMap.get(viewId));
    const view = await this.rootStore.viewService.updateWorkspaceView(workspaceSlug, viewId, data);
    const normalized = normalizeWorkspaceView(view);
    runInAction(() => {
      this.workspaceViewMap.set(view.id, normalized);
    });
    track("view_updated", {
      view_id: viewId,
      scope: "workspace",
      kind: normalized.kind,
      fields,
      source,
    });
    return view;
  }

  async deleteWorkspaceView(
    workspaceSlug: string,
    viewId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    // The kind is gone with the view, so read it first.
    const kind = this.workspaceViewMap.get(viewId)?.kind;
    await this.rootStore.viewService.deleteWorkspaceView(workspaceSlug, viewId);
    runInAction(() => {
      this.workspaceViewMap.delete(viewId);
      this.workspaceViewIdsByWorkspace.set(
        workspaceSlug,
        (this.workspaceViewIdsByWorkspace.get(workspaceSlug) ?? []).filter((id) => id !== viewId),
      );
    });
    track("view_deleted", { view_id: viewId, scope: "workspace", kind, source });
  }
}
